// useSpeechRecognition — нативный захват речи на устройстве (Web Speech API).
// aidepo-64.
//
// Альтернатива useDockRecorder (MediaRecorder + серверный whisper): здесь
// распознавание делает сам браузер, наружу отдаётся готовый текст. Аудио и
// AnalyserNode не производятся — вместо живой волны UI показывает живой
// промежуточный текст (interim). Пригодность платформы проверяется снаружи
// через voiceEngine.isNativeSttUsable() до вызова start().

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

export type NativeSttState = 'idle' | 'listening' | 'processing';

export interface UseSpeechRecognition {
  readonly state: NativeSttState;
  /** Живой промежуточный текст, пока пользователь говорит. '' когда не слушаем. */
  readonly interim: string;
  readonly error: string | null;
  /**
   * Запускает распознавание. `onFinal` зовётся один раз с финальным текстом,
   * когда речь распознана (после stop() или естественного конца фразы).
   * Резолвит false, если движок не удалось запустить.
   */
  start(onFinal: (transcript: string) => void): Promise<boolean>;
  /** Завершить фразу — финализирует распознавание (оненд → onFinal). */
  stop(): void;
  /** Сбросить ошибку/состояние в idle, не трогая активное распознавание. */
  reset(): void;
}

/** Человекочитаемое сообщение по коду ошибки SpeechRecognition. */
function describeError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Нет доступа к микрофону. Разреши доступ в настройках браузера и попробуй снова.';
    case 'no-speech':
      return 'Не расслышал — скажи ещё раз, чуть подольше.';
    case 'audio-capture':
      return 'Микрофон недоступен. Проверь, что он подключён и не занят.';
    case 'network':
      return 'Нет сети для распознавания. Проверь подключение или переключись на сервер в настройках.';
    default:
      return 'Не удалось распознать речь. Попробуй ещё раз.';
  }
}

function createRecognition(): SpeechRecognition | null {
  const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
  if (Ctor === undefined) return null;
  const rec = new Ctor();
  rec.lang = 'ru-RU';
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;
  return rec;
}

export function useSpeechRecognition(): UseSpeechRecognition {
  const [state, setState] = useState<NativeSttState>('idle');
  const [interim, setInterim] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<SpeechRecognition | null>(null);
  const finalRef = useRef<string>('');
  const erroredRef = useRef<boolean>(false);
  const onFinalRef = useRef<((t: string) => void) | null>(null);

  const teardown = useCallback((): void => {
    const rec = recRef.current;
    if (rec !== null) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.onstart = null;
    }
    recRef.current = null;
  }, []);

  // Прерываем активное распознавание при размонтировании.
  useEffect(() => {
    return () => {
      const rec = recRef.current;
      if (rec !== null) {
        try {
          rec.abort();
        } catch {
          /* уже завершено */
        }
      }
      teardown();
    };
  }, [teardown]);

  const start = useCallback(
    (onFinal: (transcript: string) => void): Promise<boolean> => {
      setError(null);
      setInterim('');
      finalRef.current = '';
      erroredRef.current = false;
      onFinalRef.current = onFinal;

      const rec = createRecognition();
      if (rec === null) {
        setError('Распознавание на устройстве недоступно.');
        return Promise.resolve(false);
      }
      recRef.current = rec;

      rec.onstart = (): void => setState('listening');

      rec.onresult = (ev: SpeechRecognitionEvent): void => {
        let interimText = '';
        for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
          const result = ev.results[i];
          if (result === undefined) continue;
          const text = result[0]?.transcript ?? '';
          if (result.isFinal) finalRef.current += text;
          else interimText += text;
        }
        setInterim(interimText);
      };

      rec.onerror = (ev: SpeechRecognitionErrorEvent): void => {
        // 'aborted' — мы сами прервали (reset/unmount), не ошибка для пользователя.
        if (ev.error === 'aborted') return;
        erroredRef.current = true;
        setError(describeError(ev.error));
      };

      rec.onend = (): void => {
        setInterim('');
        setState('idle');
        if (erroredRef.current) return;
        const text = finalRef.current.trim();
        if (text.length > 0) onFinalRef.current?.(text);
        else setError('Не расслышал — скажи ещё раз, чуть подольше.');
      };

      try {
        rec.start();
        return Promise.resolve(true);
      } catch {
        // start() кидает, если уже запущено — считаем неуспехом старта.
        teardown();
        setState('idle');
        setError('Не удалось запустить распознавание. Попробуй ещё раз.');
        return Promise.resolve(false);
      }
    },
    [teardown],
  );

  const stop = useCallback((): void => {
    const rec = recRef.current;
    if (rec === null) return;
    setState('processing');
    try {
      rec.stop();
    } catch {
      /* уже остановлено — onend разрулит */
    }
  }, []);

  const reset = useCallback((): void => {
    const rec = recRef.current;
    if (rec !== null) {
      erroredRef.current = true; // подавляем onend-фолбэк при принудительном сбросе
      try {
        rec.abort();
      } catch {
        /* noop */
      }
      teardown();
    }
    setError(null);
    setInterim('');
    setState('idle');
  }, [teardown]);

  return { state, interim, error, start, stop, reset };
}
